if (!customElements.get('upsell-products')) {
  customElements.define(
    'upsell-products',
    class UpsellProducts extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        // Wait for DOM to be ready and product-form to be defined
        this.init();
      }

      async init() {
        // Wait for product-form custom element to be defined
        await customElements.whenDefined('product-form');

        // Small delay to ensure product-form is fully initialized
        await new Promise(resolve => setTimeout(resolve, 100));

        // Find the product form in the same section
        this.productForm = document.querySelector(
          `product-form[data-section-id="${this.sectionId}"]`
        );

        if (this.productForm) {
          this.interceptFormSubmission();
        }
      }

      interceptFormSubmission() {
        const form = this.productForm.querySelector('form');
        if (!form) return;

        // Add our own submit listener with capture to run BEFORE product-form's listener
        form.addEventListener('submit', async (evt) => {
          const checkedUpsells = this.getCheckedUpsellVariants();

          // If no upsells selected, let original handler run
          if (checkedUpsells.length === 0) {
            return;
          }

          // Prevent default and stop propagation to prevent product-form handler
          evt.preventDefault();
          evt.stopImmediatePropagation();

          if (this.productForm.submitButton.getAttribute('aria-disabled') === 'true') return;

          // Handle error message reset
          if (typeof this.productForm.handleErrorMessage === 'function') {
            this.productForm.handleErrorMessage();
          }

          // Show loading state
          this.productForm.submitButton.setAttribute('aria-disabled', true);
          this.productForm.submitButton.classList.add('loading');
          const spinner = this.productForm.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');

          try {
            // Build items array for batch add
            const items = [];

            // Get main product info
            const mainVariantId = form.querySelector('[name="id"]').value;
            const quantityInput = form.querySelector('[name="quantity"]');
            const mainQuantity = quantityInput ? quantityInput.value : 1;

            // Add upsell products FIRST (they will appear after main product in cart)
            checkedUpsells.forEach((variantId) => {
              items.push({
                id: parseInt(variantId),
                quantity: 1,
              });
            });

            // Add main product LAST (it will appear first in cart)
            items.push({
              id: parseInt(mainVariantId),
              quantity: parseInt(mainQuantity),
            });

            // Make batch add request
            const response = await this.addItemsToCart(items);

            if (response.status) {
              // Error occurred
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'upsell-products',
                errors: response.errors || response.description,
                message: response.message,
              });
              if (typeof this.productForm.handleErrorMessage === 'function') {
                this.productForm.handleErrorMessage(response.description);
              }
              return;
            }

            // Success
            const cart =
              document.querySelector('cart-notification') || document.querySelector('cart-drawer');

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'upsell-products',
              productVariantId: mainVariantId,
              cartData: response,
            });

            if (cart) {
              // Render cart sections manually with null-safe parsing
              this.renderCartSections(cart, response.sections);

              // Open the cart drawer
              cart.open();
            } else {
              window.location = window.routes.cart_url;
            }
          } catch (error) {
            console.error('Upsell add to cart error:', error);
            if (typeof this.productForm.handleErrorMessage === 'function') {
              this.productForm.handleErrorMessage(error.message || 'Error adding to cart');
            }
          } finally {
            this.productForm.submitButton.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            this.productForm.submitButton.removeAttribute('aria-disabled');
          }
        }, true); // true = capture phase, runs before other listeners
      }

      getCheckedUpsellVariants() {
        const checked = [];
        // Query checkboxes fresh each time to ensure we get current state
        const checkboxes = this.querySelectorAll('.upsell-products__checkbox');
        checkboxes.forEach((checkbox) => {
          if (checkbox.checked) {
            checked.push(checkbox.dataset.variantId);
          }
        });
        return checked;
      }

      async addItemsToCart(items) {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const body = { items };

        // Add sections for cart UI update - this is key!
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });

        return response.json();
      }

      renderCartSections(cart, sections) {
        if (!sections) return;

        // Remove empty state
        const drawerInner = cart.querySelector('.drawer__inner');
        if (drawerInner) {
          drawerInner.classList.remove('is-empty');
        }
        cart.classList.remove('is-empty');

        // Render each section
        cart.getSectionsToRender().forEach((section) => {
          const html = sections[section.id];
          if (!html) return;

          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (!sectionElement) return;

          // Parse the HTML and extract the inner content
          const parsed = new DOMParser().parseFromString(html, 'text/html');

          // Try the section selector first, then .shopify-section, then body
          let content = null;
          if (section.selector) {
            content = parsed.querySelector(section.selector);
          }
          if (!content) {
            content = parsed.querySelector('.shopify-section');
          }
          if (!content) {
            content = parsed.body;
          }

          if (content) {
            sectionElement.innerHTML = content.innerHTML;
          }
        });

        // Re-attach overlay click handler
        const overlay = cart.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', cart.close.bind(cart));
        }
      }

    }
  );
}
